import React from 'react';

import { ErrorBoundary } from 'common/components/withErrorBoundary';
import { useModal as useModalGrapes } from 'common/hooks/useModalGrapes';
import { useTranslation } from 'common/hooks/useTranslation';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { type Language } from 'src/core/config/i18n';
import FEATURES from 'src/core/constants/features';
import { AnalyticalFieldMappingModalContainer } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldMappingModalContainer';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import { useAnalyticalSplitSettingsQuery } from 'src/core/modules/bookkeep/hooks/useAnalyticalSplitSettings';
import { LineSplitOnboardingGuideModal } from 'src/core/modules/bookkeep/settings/accounting/pages/LineSplitOnboardingGuide/LineSplitOnboardingGuideModal';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { useEnrichedCostCentersQuery } from './hooks/useEnrichedCostCentersQuery';
import { useMembersQuery } from './hooks/useMembersQuery';
import { CostCenterCreationMethodSelectionModal } from '../../components/CostCenterCreationMethodSelectionModal/CostCenterCreationMethodSelectionModal';
import {
  CostCenterImportModalContainer,
  type ImportType,
} from '../../components/CostCenterImportModal/CostCenterImportModalContainer';
import { CostCenterPageLoader } from '../../components/CostCenterPageLoader';
import { CostCenterSetupGuideModal } from '../../components/CostCenterSetupGuideModal';
import { CostCentersPage } from '../../components/CostCentersPage';
import { type CostCenter, type CostCenterWithBudgets } from '../../costCenter';
import { type Member } from '../../member';
import {
  CostCenterCreationFormModalContainer,
  CostCenterEditionFormModalContainer,
  CostCenterDeletionModalContainer,
} from '../CostCenterFormModalContainer';

export const CostCentersPageContainer = () => {
  const membersQueryState = useMembersQuery();
  const { t, activeLanguage } = useTranslation('global');
  const isMultyEntityConfigFeatureEnabled = useFeature(
    FEATURES.MULTI_ENTITY_CONFIG,
  );
  const isMapSyncFeatureEnabled = useFeature(FEATURES.TMP_MAP_SYNC);
  const enrichedCostCenterState = useEnrichedCostCentersQuery();
  const costCentersWithBudgets =
    enrichedCostCenterState.status === 'success'
      ? enrichedCostCenterState.data
      : [];

  const members =
    membersQueryState.status === 'success'
      ? sortMembers(membersQueryState.data, activeLanguage)
      : [];

  const costCentersWithoutMultiEntity = isMultyEntityConfigFeatureEnabled
    ? costCentersWithBudgets
    : costCentersWithBudgets.filter((cc) => !cc.isMultiEntity);

  const hasCostCenters = costCentersWithoutMultiEntity.length > 0;

  const mapping = useAnalyticalFieldMapping({
    field: {
      id: 'costCenter',
      name: t('misc.costCenter'),
    },
    fieldType: 'costCenter',
    values: costCentersWithoutMultiEntity,
    onUpdated: () => {
      hideSyncSettingsModal();
    },
    onDeleteValue: (value) => {
      showDeletionModal({
        costCenter: value,
      });
    },
  });

  const analyticalSplitSettingsQuery = useAnalyticalSplitSettingsQuery(
    isMapSyncFeatureEnabled,
  );
  const analyticalSplitSettings = analyticalSplitSettingsQuery.isLoading
    ? []
    : (analyticalSplitSettingsQuery.settings ?? []);

  const fieldExternalId = mapping.fieldMapping?.externalId;
  const lineModeState = fieldExternalId
    ? analyticalSplitSettings.find(
        (setting) => setting.externalId === fieldExternalId,
      )
    : undefined;

  const [creationModal, showCreationModal] = useModalGrapes<{
    openedFromCreationSelectionModal?: boolean;
  }>(({ isOpen, onClose, openedFromCreationSelectionModal }) => (
    <CostCenterCreationFormModalContainer
      isOpen={isOpen}
      members={members}
      onComplete={() => onClose()}
      showBackButton={openedFromCreationSelectionModal}
      onCancel={() => {
        onClose();
        track(AnalyticEventName.APPROVALS_APPROVAL_FLOW_SETUP_CANCELLED);
        if (openedFromCreationSelectionModal) {
          showCreationMethodSelectionModal();
        }
      }}
      fieldMapping={mapping.fieldMapping}
      handleValueMapping={mapping.handleValueMapping}
      isMapSyncEnabled={mapping.isMapSyncEnabled}
      syncMode={mapping.syncMode}
    />
  ));

  const [editionModal, showEditionModal] = useModalGrapes<{
    costCenter?: CostCenter;
  }>(({ isOpen, onClose, costCenter }) => {
    if (!costCenter) {
      return <div />;
    } // TODO: find the way to show animation when passing the parameter
    return (
      <CostCenterEditionFormModalContainer
        isOpen={isOpen}
        costCenter={costCenter}
        members={members}
        onComplete={() => onClose()}
        onCancel={() => {
          onClose();
          track(AnalyticEventName.APPROVALS_APPROVAL_FLOW_SETUP_CANCELLED);
        }}
        fieldMapping={mapping.fieldMapping}
        handleValueMapping={mapping.handleValueMapping}
        isMapSyncEnabled={mapping.isMapSyncEnabled}
        syncMode={mapping.syncMode}
      />
    );
  });

  const [deletionModal, showDeletionModal] = useModalGrapes<{
    costCenter?: CostCenterWithBudgets;
  }>(({ isOpen, onClose, costCenter }) => {
    if (!costCenter) {
      return <div />;
    } // TODO: find the way to show animation when passing the parameter
    return (
      <CostCenterDeletionModalContainer
        isOpen={isOpen}
        costCenter={costCenter}
        onCloseModal={() => onClose()}
        onCancel={() => onClose()}
        saveMappingChanges={mapping.onSaveChanges}
        isMapSyncEnabled={mapping.isMapSyncEnabled}
        fieldMapping={mapping.fieldMapping}
        syncMode={mapping.syncMode}
      />
    );
  });

  const [importModal, showImportModal] = useModalGrapes<{
    openedFromCreationSelectionModal?: boolean;
    type: ImportType;
  }>(
    ({
      isOpen,
      onClose,
      type = 'create',
      openedFromCreationSelectionModal,
    }) => {
      return (
        <CostCenterImportModalContainer
          type={type}
          onExit={() => {
            onClose();
            if (openedFromCreationSelectionModal) {
              showCreationMethodSelectionModal();
            }
          }}
          onCancel={onClose}
          onSuccess={() => {
            onClose();
          }}
          isOpen={isOpen}
          showBackButton={openedFromCreationSelectionModal}
        />
      );
    },
  );

  const [
    creationMethodSelectionModal,
    showCreationMethodSelectionModal,
    hideCreationMethodSelectionModal,
  ] = useModalGrapes(({ isOpen, onClose }) => {
    return (
      <CostCenterCreationMethodSelectionModal
        isOpen={isOpen}
        isMapSyncEnabled={mapping.isMapSyncEnabled}
        onCancel={() => onClose()}
        onSelect={(method) => {
          hideCreationMethodSelectionModal();

          if (method === 'import') {
            showImportModal({
              type: hasCostCenters ? 'edit' : 'create',
              openedFromCreationSelectionModal: true,
            });
          } else {
            showCreationModal({ openedFromCreationSelectionModal: true });
          }
        }}
      />
    );
  });

  const [setupGuideModal, showSetupGuideModal, hideSetupGuideModal] =
    useModalGrapes<{ onDone?(): void }>(({ isOpen, onDone = () => null }) => (
      <CostCenterSetupGuideModal
        isOpen={isOpen}
        onCloseModal={() => hideSetupGuideModal()}
        onDone={() => {
          hideSetupGuideModal();
          onDone();
        }}
      />
    ));

  const [syncSettingsModal, showSyncSettingsModal, hideSyncSettingsModal] =
    useModalGrapes(({ isOpen }) => (
      <AnalyticalFieldMappingModalContainer
        isOpen={isOpen}
        onClose={() => hideSyncSettingsModal()}
        isSplittable
        internalField={{ id: 'costCenter', name: t('misc.costCenter') }}
        values={costCentersWithBudgets}
        fieldType="costCenter"
        mapping={mapping}
      />
    ));

  const [onboardingGuideModal, showOnboardingGuideModal] = useModalGrapes(
    ({ isOpen, onClose }) => (
      <LineSplitOnboardingGuideModal
        isOpen={isOpen}
        internalField={{ id: 'costCenter', name: t('misc.costCenter') }}
        isSplittable
        {...mapping}
        onClose={onClose}
        fieldType="costCenter"
        exportLevel={
          lineModeState?.sendOnHeader && lineModeState.sendOnLine
            ? 'headerLine'
            : 'header'
        }
      />
    ),
  );

  if (
    enrichedCostCenterState.status !== 'success' ||
    membersQueryState.status !== 'success' ||
    mapping.isFieldMappingsLoading
  ) {
    return <CostCenterPageLoader />;
  }

  const shouldShowLineSplitWarning = !!lineModeState?.sendOnHeader;
  return (
    <ErrorBoundary
      context={{ team: 'finance-controller', scope: 'cost-centers' }}
    >
      <div className="page__container">
        <CostCentersPage
          members={members}
          costCenters={mapping.sortedValues}
          externalValues={mapping.externalValues}
          showWarningCallout={mapping.showWarningCallout}
          showLineSplitWarningCallout={shouldShowLineSplitWarning}
          lineSplitConfiguration={lineModeState}
          externalFieldName={mapping.fieldMapping?.externalName}
          syncMode={mapping.syncMode}
          isIntegrationNameLoading={mapping.isIntegrationNameLoading}
          isMapSyncEnabled={mapping.isMapSyncEnabled}
          onClickFirstCreate={() =>
            showSetupGuideModal({
              onDone: showCreationMethodSelectionModal,
            })
          }
          onClickCreate={() => {
            if (hasCostCenters) {
              return showCreationModal();
            }
            showSetupGuideModal({ onDone: showCreationModal });
          }}
          onClickMoreInfo={showSetupGuideModal}
          onClickEdit={(costCenter) => {
            showEditionModal({ costCenter });
          }}
          onClickDelete={(costCenter) => {
            showDeletionModal({ costCenter });
          }}
          onClickImport={() =>
            hasCostCenters
              ? showImportModal({ type: 'edit' })
              : showSetupGuideModal({
                  onDone: () => showImportModal({ type: 'create' }),
                })
          }
          onReviewSettingsClick={() => showSyncSettingsModal()}
          onReviewLineSplitSettingsClick={() => showOnboardingGuideModal()}
        />
        {creationModal}
        {editionModal}
        {deletionModal}
        {setupGuideModal}
        {creationMethodSelectionModal}
        {importModal}
        {syncSettingsModal}
        {onboardingGuideModal}
      </div>
    </ErrorBoundary>
  );
};

const sortMembers = (members: Member[], activeLanguage: Language) => {
  return members.sort((m1, m2) => {
    return (
      m1.fullname.localeCompare(m2.fullname, activeLanguage, {
        sensitivity: 'base',
      }) ||
      m1.email.localeCompare(m2.email, activeLanguage, {
        sensitivity: 'base',
      })
    );
  });
};
